/**
 * `<caos-error-panel>` — a failed surface that leads with a human message, says whose
 * problem it is, offers a retry when there is one, and keeps its code and correlation id
 * as secondary, receipt-style detail.
 * (Catalog: Status & feedback › ErrorPanel — leads with the message, says whose problem it is, and offers a way forward.)
 *
 * CAOS-579 — the decided error model, expressed: a `heading`, a `message` (the default
 * slot) as the HEADLINE — never the code — a `fault` (`user` / `admin` / `platform`) that
 * says who can act on it, a `retriable` flag that draws an in-place retry when the caller
 * has not slotted a control of its own, an optional `docs-href` to the relevant guide, and
 * a mono diagnostics line (`code` and/or `correlation-id`) kept secondary and framed as a
 * RECEIPT — proof the failure is already recorded — never as a token somebody is expected
 * to read aloud or relay to another person. The panel surface stays neutral (structure from
 * a 1px border, the design-system rule); the danger is carried by the icon and a small
 * eyebrow that read the SEMANTIC danger tokens with the theme-fixed on-tint INK
 * (`--caos-color-danger-text` on `--caos-color-danger-bg`) so the eyebrow is ≥4.5:1 in
 * both themes. The container is a labelled `role="alert"` region. Styling seams:
 * `::part(panel)`, `::part(icon)`, `::part(eyebrow)`, `::part(title)`, `::part(message)`,
 * `::part(fault)`, `::part(diagnostics)`, `::part(actions)`, `::part(retry)`, `::part(guide)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

let uid = 0;

/** The three faults an error can carry (kernel/src/errors/axes.ts `Fault`), and what the
 *  screen says on the strength of each — never "contact support", per the decided model. */
const FAULT_COPY: Readonly<Record<string, string>> = {
  user: 'You can fix this.',
  admin: 'Your administrator can fix this.',
  platform: "This one's on us — we're already on it.",
};

const sheet = makeSheet(`
  :host { display: block; }

  .panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--caos-space-3);
    padding: var(--caos-space-7) var(--caos-space-5);
    max-width: 460px;
    margin: 0 auto;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-lg);
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: var(--caos-radius-pill);
    /* soft danger disc; the glyph is the saturated danger hue (icon contrast idiom) */
    background: var(--caos-color-danger-bg);
    color: var(--caos-color-danger);
  }
  .icon svg { display: block; width: 24px; height: 24px; }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    font-family: var(--caos-font-mono);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: var(--caos-radius-pill);
    /* text ON the danger tint uses the theme-fixed on-tint ink (AF2 lesson) */
    background: var(--caos-color-danger-bg);
    color: var(--caos-color-danger-text);
  }

  .title {
    font-family: var(--caos-font-display);
    font-weight: 600;
    font-size: 17px;
    line-height: 1.3;
    color: var(--caos-color-text);
  }
  .title:empty { display: none; }

  .message { font-size: 13px; line-height: 1.55; color: var(--caos-color-text-muted); }

  /* Whose problem this is — a plain-text line, deliberately not a coloured badge: the
     eyebrow already carries the alarm, and a second tinted chip would compete with it. */
  .fault { font-size: 12.5px; font-weight: 600; color: var(--caos-color-text); }
  .fault:empty { display: none; }

  .diagnostics {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-family: var(--caos-font-mono);
    font-size: 11.5px;
    line-height: 1.5;
    color: var(--caos-color-text-muted);
    background: var(--caos-color-surface-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    padding: var(--caos-space-2) var(--caos-space-3);
    word-break: break-all;
  }
  .diagnostics:not(:has(*)) { display: none; }
  .diagnostics .k { color: var(--caos-color-text); font-weight: 500; }
  /* The receipt line: prose, NOT mono — it is the one sentence in the block that is meant to
     be read rather than copied, and setting it apart from the mono rows is what keeps the
     reference reading as "this is recorded" rather than as one more value to transcribe. */
  .diagnostics .receipt {
    font-family: var(--caos-font);
    color: var(--caos-color-text-muted);
    margin-top: 2px;
  }

  .actions { display: flex; align-items: center; gap: var(--caos-space-2); margin-top: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }

  .guide {
    font-family: var(--caos-font);
    font-size: 13px;
    font-weight: 600;
    color: var(--caos-color-accent);
    text-decoration: none;
  }
  .guide:hover { text-decoration: underline; }
  .guide:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }

  /* The DEFAULT retry control -- drawn only when the caller has slotted nothing of its own
     into the action slot, so a page free to hand-compose its own controls (see the "way
     forward" example) never ends up with two. Styled as a primary action, matching the
     weight a first-class "try again" affordance should carry. */
  .retry {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: var(--caos-radius-sm);
    /* CAOS-1551 — accent-fill under white text: white on accent is 3.60:1, below AA. */
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    border: 1px solid var(--caos-color-accent-fill);
    cursor: pointer;
    font-family: var(--caos-font);
    font-size: 13px;
    font-weight: 600;
  }
  /* CAOS-1551 — accent-fill-hover, not accent-strong: accent-strong is lightened on dark and
     this white label measures 4.48:1 there, just under the floor. */
  .retry:hover { background: var(--caos-color-accent-fill-hover); }
  .retry:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

export class CaosErrorPanel extends CaosElement {
  static get observedAttributes(): string[] {
    return ['heading', 'eyebrow', 'code', 'correlation-id', 'fault', 'retriable', 'docs-href'];
  }

  private titleEl: HTMLDivElement;
  private eyebrowEl: HTMLSpanElement;
  private faultEl: HTMLDivElement;
  private diag: HTMLDivElement;
  private actionSlot: HTMLSlotElement;
  private retryBtn: HTMLButtonElement;
  private guideLink: HTMLAnchorElement;
  private readonly seq = ++uid;

  constructor() {
    super(sheet);

    const panel = document.createElement('div');
    panel.className = 'panel';
    panel.setAttribute('part', 'panel');
    panel.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.setAttribute('part', 'icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.append(makeIcon('danger'));

    this.eyebrowEl = document.createElement('span');
    this.eyebrowEl.className = 'eyebrow';
    this.eyebrowEl.setAttribute('part', 'eyebrow');

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    this.titleEl.id = `caos-error-title-${this.seq}`;
    panel.setAttribute('aria-labelledby', this.titleEl.id);

    const message = document.createElement('div');
    message.className = 'message';
    message.setAttribute('part', 'message');
    message.append(document.createElement('slot'));

    // CAOS-579 — whose problem this is, said plainly, on the strength of the error's own
    // `fault` axis rather than left for the message prose to carry alone.
    this.faultEl = document.createElement('div');
    this.faultEl.className = 'fault';
    this.faultEl.setAttribute('part', 'fault');

    this.diag = document.createElement('div');
    this.diag.className = 'diagnostics';
    this.diag.setAttribute('part', 'diagnostics');

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');

    // The default retry — drawn first so a caller's own slotted controls (the "way forward"
    // pattern) read after it, then hidden in `sync()` whenever the caller has slotted its own.
    this.retryBtn = document.createElement('button');
    this.retryBtn.type = 'button';
    this.retryBtn.className = 'retry';
    this.retryBtn.setAttribute('part', 'retry');
    this.retryBtn.textContent = 'Try again';
    this.retryBtn.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('retry', { bubbles: true, composed: true }));
    });

    this.actionSlot = document.createElement('slot');
    this.actionSlot.name = 'action';
    // A caller slotting its own control(s) after the panel is already connected — the common
    // case, a host building the action from data that arrives later — has to retract the
    // default retry the same way setting `retriable` late has to draw it.
    this.actionSlot.addEventListener('slotchange', () => this.syncRetry());

    this.guideLink = document.createElement('a');
    this.guideLink.className = 'guide';
    this.guideLink.setAttribute('part', 'guide');
    this.guideLink.textContent = 'Read the guide';
    this.guideLink.rel = 'noopener';

    actions.append(this.retryBtn, this.actionSlot, this.guideLink);

    panel.append(icon, this.eyebrowEl, this.titleEl, message, this.faultEl, this.diag, actions);
    this.root.append(panel);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** Whether the caller has slotted its own `action` content — if so, that stands ALONE; a
   *  default retry alongside a caller's own "Try again" would be two controls for one job. */
  private hasOwnActions(): boolean {
    return this.actionSlot.assignedNodes().length > 0;
  }

  private syncRetry(): void {
    const retriable = this.hasAttribute('retriable');
    this.retryBtn.style.display = retriable && !this.hasOwnActions() ? '' : 'none';
  }

  private sync(): void {
    this.titleEl.textContent = this.getAttribute('heading') ?? '';
    const eyebrow = this.getAttribute('eyebrow') ?? 'Error';
    this.eyebrowEl.textContent = eyebrow;
    this.eyebrowEl.style.display = eyebrow ? '' : 'none';

    const fault = this.getAttribute('fault');
    this.faultEl.textContent = fault ? (FAULT_COPY[fault] ?? '') : '';

    this.syncRetry();

    const docsHref = this.getAttribute('docs-href');
    if (docsHref) {
      this.guideLink.href = docsHref;
      this.guideLink.style.display = '';
    } else {
      this.guideLink.removeAttribute('href');
      this.guideLink.style.display = 'none';
    }

    // diagnostics: a labelled code and/or correlation id, framed as a RECEIPT — proof the
    // failure is already recorded — never as a token somebody reads aloud or relays onward.
    this.diag.replaceChildren();
    const code = this.getAttribute('code');
    const cid = this.getAttribute('correlation-id');
    const addRow = (label: string, value: string): void => {
      const row = document.createElement('div');
      const k = document.createElement('span');
      k.className = 'k';
      k.textContent = `${label} `;
      row.append(k, document.createTextNode(value));
      this.diag.append(row);
    };
    if (code) addRow('Code', code);
    if (cid) {
      addRow('Recorded as', cid);
      // The receipt sentence — the one line in the block meant to be READ, not copied. Says
      // this is already on file rather than something the reader is expected to relay.
      const receipt = document.createElement('div');
      receipt.className = 'receipt';
      receipt.textContent = "This has already been logged. You don't need to write it down.";
      this.diag.append(receipt);
    }
  }
}

define('caos-error-panel', CaosErrorPanel);

declare global {
  interface HTMLElementTagNameMap {
    'caos-error-panel': CaosErrorPanel;
  }
}
